/* SWAAP room scroll — dependency-free, safe to load more than once in a theme. */
(() => {
  if (customElements.get('swaap-room-scroll')) return;
  class SwaapRoomScroll extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.video = this.querySelector('video');
      this.stage = this.querySelector('.swaap-room__stage');
      this.intro = this.querySelector('.swaap-room__intro');
      this.outro = this.querySelector('.swaap-room__outro');
      this.skip = this.querySelector('[data-room-skip]');
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.progress = 0;
      this.target = 0;
      this.ready = false;
      this.raf = 0;
      const options = { signal: this.abort.signal };
      this.update = () => {
        if (this.staticMode) return;
        const distance = Math.max(1, this.offsetHeight - this.stage.offsetHeight);
        const top = parseFloat(getComputedStyle(this.stage).top) || 0;
        this.target = Math.max(0, Math.min(1, (top - this.getBoundingClientRect().top) / distance));
        this.schedule();
      };
      this.onSeek = () => { this.seekBusy = false; this.dataset.displayTime = this.video.currentTime.toFixed(3); this.schedule(); };
      this.video.addEventListener('seeked', this.onSeek, options);
      this.video.addEventListener('loadeddata', () => {
        this.ready = true;
        this.classList.add('is-ready');
        this.update();
      }, options);
      this.video.addEventListener('error', () => this.showStatic('error'), options);
      this.skip?.addEventListener('click', () => {
        // Sauter jusqu'au premier élément réellement visible qui suit la section
        // (le <noscript> voisin et les blocs vides ne comptent pas).
        const firstVisible = (node) => {
          while (node && !node.offsetHeight) node = node.nextElementSibling;
          return node;
        };
        const root = this.closest('.shopify-section') || this.parentElement || this;
        const destination = firstVisible(this.nextElementSibling) || firstVisible(root.nextElementSibling);
        if (destination) {
          destination.scrollIntoView({ behavior: this.reduce.matches ? 'instant' : 'smooth', block: 'start' });
          if (!destination.hasAttribute('tabindex')) destination.setAttribute('tabindex', '-1');
          destination.focus({ preventScroll: true });
        } else scrollTo({ top: scrollY + this.getBoundingClientRect().bottom, behavior: 'smooth' });
      }, options);
      window.addEventListener('scroll', this.update, { ...options, passive: true });
      window.addEventListener('resize', this.update, { ...options, passive: true });
      this.reduce.addEventListener('change', () => this.configure(), options);
      this.mobile.addEventListener('change', () => this.configure(), options);
      this.configure();
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this);
    }
    configure() {
      this.staticMode = false;
      this.classList.remove('is-static', 'is-error');
      const saveData = navigator.connection?.saveData;
      if (this.reduce.matches || saveData) return this.showStatic('preference');
      const src = this.mobile.matches && this.dataset.mobileSrc ? this.dataset.mobileSrc : this.dataset.desktopSrc;
      if (!src) return this.showStatic('missing');
      this.classList.add('is-enhanced');
      // L'affiche suit le format : verticale en dessous de 750 px, horizontale au-dessus.
      this.video.poster = this.mobile.matches
        ? (this.dataset.mobilePoster || this.video.poster)
        : (this.dataset.desktopPoster || this.video.poster);
      if (this.video.getAttribute('src') !== src) {
        this.ready = false;
        this.seekBusy = false;
        this.classList.remove('is-ready');
        this.video.src = src;
        this.video.muted = true;
        this.video.load();
      }
      this.update();
    }
    showStatic(reason) {
      this.staticMode = true;
      this.classList.add('is-static');
      this.classList.toggle('is-error', reason === 'error');
      this.classList.remove('is-enhanced');
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      this.style.setProperty('--room-progress', '1');
      if (this.intro) { this.intro.style.opacity = '0'; this.intro.inert = true; }
      if (this.outro) { this.outro.style.opacity = '1'; this.outro.inert = false; }
    }
    schedule() { if (!this.raf && !this.staticMode) this.raf = requestAnimationFrame(() => this.tick()); }
    tick() {
      this.raf = 0;
      if (this.staticMode) return;
      const delta = this.target - this.progress;
      this.progress = Math.abs(delta) < 0.001 ? this.target : this.progress + delta * 0.24;
      this.dataset.progress = this.progress.toFixed(4);
      this.style.setProperty('--room-progress', String(this.progress));
      const introOpacity = Math.max(0, 1 - this.progress / 0.18);
      const outroOpacity = Math.max(0, Math.min(1, (this.progress - 0.89) / 0.09));
      if (this.intro) { this.intro.style.opacity = String(introOpacity); this.intro.inert = introOpacity < 0.1; }
      if (this.outro) { this.outro.style.opacity = String(outroOpacity); this.outro.inert = outroOpacity < 0.9; }
      if (this.ready && Number.isFinite(this.video.duration)) {
        const fps = Number(this.dataset.fps) || 24;
        const end = Math.max(0, this.video.duration - 1 / fps);
        const wanted = Math.min(end, Math.round(this.progress * end * fps) / fps);
        if (!this.seekBusy && Math.abs(this.video.currentTime - wanted) > 0.5 / fps) {
          this.seekBusy = true;
          this.video.currentTime = wanted;
          clearTimeout(this.seekTimer);
          this.seekTimer = setTimeout(() => { this.seekBusy = false; this.schedule(); }, 1000);
        }
      }
      if (Math.abs(this.target - this.progress) > 0.0001) this.schedule();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.raf);
      clearTimeout(this.seekTimer);
      this.video?.pause();
    }
  }
  customElements.define('swaap-room-scroll', SwaapRoomScroll);
})();
